import React from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogActions, DialogBody, DialogDescription, DialogTitle } from "src/components/base/dialog";
import Form from "src/components/base/form";
import {
    Description,
    ErrorMessage,
    Field,
    FieldGroup,
    Fieldset,
    Label,
    RequiredLabel,
} from "src/components/base/fieldset";
import { Button, PrimaryButton } from "src/components/base/button";
import { useForm } from "@tanstack/react-form";
import { Combobox, ComboboxOption } from "src/components/base/combobox";
import { useDebouncedState } from "src/utils/state";
import { ApiInternal } from "src/api/api_internal";
import { FullDevice, SimpleDeviceGroup } from "src/api/generated/frontend_internal_v1";

/**
 * The properties for {@link DeviceGroupLinkDeviceDialog}
 */
export type DeviceGroupLinkDeviceDialogProps = {
    /** Whether the dialog is open */
    isOpen: boolean;
    /** Callback when the popup should be closed (only if not successful) */
    onClose: () => void;
    /** Device group to link the device to */
    deviceGroup: SimpleDeviceGroup;
    /** Callback when the device was linked successfully (if not closed otherwise) */
    onSuccess: () => void;
};

/**
 * Dialog to add ("link") a device to a device group
 */
export default function DeviceGroupLinkDeviceDialog(props: DeviceGroupLinkDeviceDialogProps) {
    const [t] = useTranslation("dialog-device-group-link-device");
    const [tg] = useTranslation();

    const [_, debouncedQuery, setDebouncedQuery] = useDebouncedState("", 500);
    const [devices, setDevices] = React.useState<Array<FullDevice>>([]);

    const form = useForm({
        defaultValues: {
            device: null as null | FullDevice,
        },
        validators: {
            onSubmitAsync: async ({ value }) => {
                if (!value.device) {
                    return {
                        fields: {
                            device: t("error.field-must-be-filled"),
                        },
                    };
                }

                if (!value.device.user) {
                    return {
                        fields: {
                            device: t("error.device-not-owned-by-user"),
                        },
                    };
                }

                if (value.device.device_groups?.some((d) => props.deviceGroup.uuid === d.uuid)) {
                    return {
                        fields: {
                            device: t("error.device-already-in-device-group"),
                        },
                    };
                }

                await ApiInternal.devices.addToDeviceGroup(value.device.uuid, props.deviceGroup.uuid);
                props.onSuccess();
            },
        },
    });

    /**
     * Query the devices to display in the dropdown
     *
     * Only devices owned by a user are eligible, the backend rejects
     * adding servers to device groups.
     */
    const queryDevices = async () => {
        const res = await ApiInternal.devices.getAll();
        setDevices(
            res.items.filter(
                (device) =>
                    device.user !== null && !device.device_groups?.some((d) => props.deviceGroup.uuid === d.uuid),
            ),
        );
    };

    React.useEffect(() => {
        queryDevices().then();
    }, [props.deviceGroup, props.isOpen]);

    // The endpoint has no filters, so the search query is applied client-side
    const query = debouncedQuery.trim().toLowerCase();
    const shownDevices = query ? devices.filter((d) => d.dnsname.toLowerCase().includes(query)) : devices;

    React.useEffect(() => {
        form.reset();
    }, [props.deviceGroup, props.isOpen]);

    return (
        <Dialog open={props.isOpen} onClose={props.onClose}>
            <DialogTitle>
                {t("heading.link-new-device-to-device-group", { deviceGroup: props.deviceGroup.name })}
            </DialogTitle>
            <DialogDescription>{t("description.link-new-device")}</DialogDescription>
            <DialogBody>
                <Form onSubmit={form.handleSubmit}>
                    <Fieldset>
                        <FieldGroup>
                            <form.Field name={"device"}>
                                {(fieldApi) => (
                                    <Field>
                                        <RequiredLabel>{t("label.existing-device")}</RequiredLabel>
                                        <Combobox
                                            value={fieldApi.state.value}
                                            onChange={(v) => fieldApi.handleChange(v)}
                                            onClose={() => setDebouncedQuery("")}
                                            queryDisplay={(d) => d?.dnsname || ""}
                                            onQueryChange={(q) => setDebouncedQuery(q)}
                                            invalid={fieldApi.state.meta.errors.length > 0}
                                            disabled={devices.length === 0}
                                        >
                                            {shownDevices.map((device) => (
                                                <ComboboxOption value={device} key={device.uuid}>
                                                    <Label>{device.dnsname}</Label>
                                                </ComboboxOption>
                                            ))}
                                        </Combobox>
                                        {devices.length === 0 && (
                                            <Description>{t("description.no-eligible-devices")}</Description>
                                        )}
                                        {fieldApi.state.meta.errors.map((err) => (
                                            <ErrorMessage>{err}</ErrorMessage>
                                        ))}
                                    </Field>
                                )}
                            </form.Field>

                            <DialogActions>
                                <Button plain={true} onClick={props.onClose}>
                                    {tg("button.cancel")}
                                </Button>
                                <PrimaryButton type={"submit"}>{tg("button.save")}</PrimaryButton>
                            </DialogActions>
                        </FieldGroup>
                    </Fieldset>
                </Form>
            </DialogBody>
        </Dialog>
    );
}
